/**
 * Send to Phone Modal
 * 
 * Displays QR code for Call/WhatsApp actions
 * Desktop = brain, Phone = execution
 */

import { QRCodeSVG } from 'qrcode.react';
import { Phone, MessageCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import { buildWhatsAppLink, buildTelLink } from '@/core/decision-engine/eliteEngine';
import { Lead } from '@/hooks/useLeads';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useAuth } from '@/lib/auth';

interface SendToPhoneModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  dealValue?: number;
  actionType: 'call' | 'whatsapp';
}

export function SendToPhoneModal({
  open,
  onOpenChange,
  lead,
  dealValue,
  actionType,
}: SendToPhoneModalProps) {
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  // Build URL based on action type
  const url = actionType === 'call' 
    ? buildTelLink(lead.phone || '')
    : buildWhatsAppLink(lead.phone || '');

  const actionLabel = actionType === 'call' ? 'Call' : 'WhatsApp';
  const ActionIcon = actionType === 'call' ? Phone : MessageCircle;

  // Track when modal opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && activeWorkspaceId && user?.id) {
      // Log event
      logSendToPhoneEvent(activeWorkspaceId, user.id, lead.id, actionType);
    }
    onOpenChange(newOpen);
  };

  if (!url) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-center">Scan to act now</DialogTitle>
          <DialogDescription className="text-center">
            {lead.name}
            {dealValue && dealValue > 0 && (
              <span className="ml-2 font-semibold text-primary">
                {formatCurrency(dealValue)}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-4">
          {/* QR Code */}
          <div className="p-4 bg-white rounded-lg">
            <QRCodeSVG
              value={url}
              size={200}
              level="H"
              includeMargin={false}
            />
          </div>

          {/* Action label */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ActionIcon className="w-4 h-4" />
            <span>{actionLabel}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Log send_to_phone_opened event
async function logSendToPhoneEvent(
  workspaceId: string,
  userId: string,
  leadId: string,
  actionType: 'call' | 'whatsapp'
) {
  try {
    // Check if action_events table exists
    const testQuery = await supabase
      .from('action_events')
      .select('id')
      .limit(1);

    if (testQuery.error && (testQuery.error.code === '42P01' || testQuery.error.message?.includes('does not exist'))) {
      logger.warn('[SendToPhoneModal] action_events table does not exist, skipping log', {
        leadId,
        workspaceId,
      });
      return;
    }

    // Insert event
    const { error } = await supabase
      .from('action_events')
      .insert({
        workspace_id: workspaceId,
        user_id: userId,
        lead_id: leadId,
        action_type: 'send_to_phone_opened',
        metadata: {
          action_type: actionType,
        },
        created_at: new Date().toISOString(),
      });

    if (error) {
      logger.error('[SendToPhoneModal] Failed to log event', error, {
        leadId,
        workspaceId,
      });
    } else {
      logger.info('[SendToPhoneModal] Event logged', {
        leadId,
        workspaceId,
        actionType,
      });
    }
  } catch (error: any) {
    logger.error('[SendToPhoneModal] Error logging event', error, {
      leadId,
      workspaceId,
    });
  }
}

